import React from "react";
import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";

// A flat list of what is wrong, deliberately NOT an annotated copy of the data.
//
// The alternative — highlighting offending cells inside `DataPreviewTable` — was rejected. Java reports the
// problem against a row-DTO path (`consumption.measureId`), and inverting the descriptor's `row_path` back
// to the spreadsheet column it came from is ambiguous: several frame columns can feed one nested field, and
// a required field can be missing precisely because no column mapped to it. Highlighting the wrong cell is
// worse than highlighting none, because the user would go and "fix" data that is fine.
//
// Two sources feed this, and they use different casing: Python's `CommitPayloadIssue` (`row_index`) and
// Java's `AiIngestRowErrorDto` (`rowIndex`, plus `rejectedValue`). `normalizeIssues` flattens both so the
// table has one shape to render.

/**
 * @param {Array<object>} issues raw issues from either source
 * @returns {Array<{key: string, row: number|null, field: string|null, column: string|null,
 *   message: string, rejectedValue: string|null}>}
 */
export function normalizeIssues(issues) {
  if (!Array.isArray(issues)) return [];
  return issues.map((issue, index) => {
    // A 0-based wire index; humans count rows from 1. `?? null` rather than `|| null` — row 0 is real.
    const rawRow = issue.rowIndex ?? issue.row_index;
    const rejected = issue.rejectedValue ?? issue.rejected_value;
    return {
      // Index-suffixed: two rows can legitimately fail the same field the same way, so the tuple alone is
      // not unique.
      key: `${rawRow ?? "-"}:${issue.field ?? issue.column ?? issue.code ?? "?"}:${index}`,
      row: Number.isInteger(rawRow) ? rawRow + 1 : null,
      field: issue.field ?? null,
      column: issue.column ?? null,
      message: issue.message ?? issue.code ?? "Rejected",
      rejectedValue:
        rejected === null || rejected === undefined || rejected === "" ? null : String(rejected),
    };
  });
}

export const IngestIssuesTable = ({ issues, droppedCount = 0 }) => {
  const rows = normalizeIssues(issues);
  if (rows.length === 0) return null;

  // A whole-frame problem (a missing required column, no target) carries no row number. Column headers that
  // would be empty for every row are noise, so they are only rendered when something fills them.
  const showRow = rows.some((r) => r.row !== null);
  const showValue = rows.some((r) => r.rejectedValue !== null);

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      overflow="auto"
      maxH="320px"
      w="100%"
      sx={{
        "&::-webkit-scrollbar": { width: "6px", height: "6px" },
        "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
        "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
        scrollbarWidth: "thin",
      }}
    >
      <Table size="sm" variant="simple">
        <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
          <Tr>
            {showRow && (
              <Th fontSize="11px" color="gray.500" w="72px">
                Row
              </Th>
            )}
            <Th fontSize="11px" color="gray.500" w="30%">
              Field
            </Th>
            <Th fontSize="11px" color="gray.500">
              Problem
            </Th>
            {showValue && (
              <Th fontSize="11px" color="gray.500" w="20%">
                Value
              </Th>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((r) => (
            <Tr key={r.key}>
              {showRow && (
                <Td fontSize="12px" color="gray.600">
                  {r.row ?? "—"}
                </Td>
              )}
              <Td fontSize="12px">
                <Text as="span" fontFamily="mono" color="darkTeal">
                  {r.field ?? r.column ?? "—"}
                </Text>
                {/* Both present means we know the frame column that fed this field — worth showing, since
                    it is the one thing the user can act on directly. */}
                {r.field && r.column && (
                  <Text fontSize="11px" color="gray.400">
                    from &ldquo;{r.column}&rdquo;
                  </Text>
                )}
              </Td>
              <Td fontSize="12px" color="gray.700">
                {r.message}
              </Td>
              {showValue && (
                <Td fontSize="12px" color="gray.600" fontFamily="mono">
                  {r.rejectedValue ?? "—"}
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
      {droppedCount > 0 && (
        <Box px={3} py={2} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
          <Text fontSize="11px" color="gray.500">
            {droppedCount.toLocaleString()} further {droppedCount === 1 ? "issue" : "issues"} not shown.
            Fixing these usually fixes those too — they are typically the same mistake repeated.
          </Text>
        </Box>
      )}
    </Box>
  );
};
